import { useEffect, useState } from "react";
import { api, ApiError, type AvaliacaoLink, type RascunhoAvaliacao } from "../api";
import { IconClose, IconCheck } from "../icons";
import { ordenarPorNome } from "../ordem";

/**
 * O QUE O GESTOR SALVOU E NÃO ENVIOU.
 *
 * O link do gestor tem dois botões: "Salvar" (por pessoa) e "Concluir e enviar
 * ao RH". Só o segundo cria a avaliação — o "Salvar" grava um RASCUNHO. Gestor
 * que preencheu todo mundo, viu "Salvo 14:32" e fechou a página deixava o
 * trabalho inteiro invisível: o RH só via a contagem "N salvo(s) sem enviar".
 *
 * Esta tela mostra esse preenchimento e deixa o RH FECHAR pelo gestor — o que
 * estava salvo vira avaliação, com o gestor do link como avaliador.
 */
export default function RascunhoLinkModal({
  link,
  onClose,
  onEfetivado,
}: {
  link: AvaliacaoLink;
  onClose: () => void;
  onEfetivado: () => void;
}) {
  const [rascunhos, setRascunhos] = useState<RascunhoAvaliacao[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [periodo, setPeriodo] = useState(link.periodo ?? "");

  useEffect(() => {
    let cancel = false;
    api.avaliacaoLinks
      .rascunhos(link.id)
      .then((d) => !cancel && setRascunhos(d.rascunhos ?? []))
      .catch((e) => !cancel && setErro(e instanceof ApiError ? e.message : "Erro ao carregar."));
    return () => {
      cancel = true;
    };
  }, [link.id]);

  const aproveitaveis = (rascunhos ?? []).filter((r) => r.aproveitavel).length;

  async function efetivar() {
    if (
      !confirm(
        `Fechar a avaliação de ${link.gestor_nome} com o que já está preenchido?\n\n` +
          `${aproveitaveis} colaborador(es) viram avaliação. O link deixa de aceitar novo envio.`,
      )
    )
      return;
    setSalvando(true);
    setErro(null);
    try {
      await api.avaliacaoLinks.efetivar(link.id, periodo.trim() || undefined);
      onEfetivado();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível efetivar.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-ficha" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Preenchido e não enviado — {link.gestor_nome}</h3>
          <button type="button" className="icon-btn" onClick={onClose} title="Fechar">
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body avaliacoes-historico">
          <p className="hint">
            O gestor salvou este preenchimento mas não clicou em <strong>“Concluir e enviar ao RH”</strong>,
            então ainda não é avaliação. Você pode conferir aqui e fechar por ele — o que está salvo vira
            avaliação, com <strong>{link.gestor_nome}</strong> como avaliador.
          </p>

          {erro && <div className="form-error">{erro}</div>}

          {!rascunhos ? (
            <div className="empty">Carregando…</div>
          ) : rascunhos.length === 0 ? (
            <div className="empty">Nada salvo neste link.</div>
          ) : (
            <>
              <label className="field">
                <span>Período (fica gravado na avaliação)</span>
                <input
                  value={periodo}
                  onChange={(e) => setPeriodo(e.target.value)}
                  placeholder="ex.: Agosto/2026"
                />
              </label>

              {ordenarPorNome(rascunhos, (r) => r.colaborador_nome).map((r) => (
                <RascunhoPessoa key={r.colaborador_id} r={r} />
              ))}
            </>
          )}
        </div>

        <div className="modal-foot">
          {aproveitaveis > 0 && (
            <button type="button" className="btn-primary" onClick={efetivar} disabled={salvando}>
              <IconCheck size={16} />{" "}
              {salvando ? "Efetivando…" : `Efetivar ${aproveitaveis} avaliação(ões)`}
            </button>
          )}
          <button type="button" className="btn-secondary" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

/** Um colaborador do rascunho: nota que sairia e as respostas já dadas. */
function RascunhoPessoa({ r }: { r: RascunhoAvaliacao }) {
  const [aberto, setAberto] = useState(false);
  const respostas = r.criterios ?? {};
  const questoes = (r.modelo?.questoes ?? []).filter((q) => respostas[q.chave] != null);

  return (
    <div className="aval-item">
      <button type="button" className="aval-item-head" onClick={() => setAberto((v) => !v)}>
        <span className="aval-nota">{r.nota != null ? Number(r.nota).toFixed(1) : "—"}</span>
        <span className="aval-item-info">
          <strong>{r.colaborador_nome ?? `Colaborador #${r.colaborador_id}`}</strong>
          <small>
            {r.cargo || "—"}
            {r.aproveitavel ? "" : " · salvo SEM nota (não vira avaliação)"}
          </small>
        </span>
        <span className="aval-item-toggle">{aberto ? "Fechar" : "Ver o que preencheu"}</span>
      </button>

      {aberto && (
        <div className="aval-item-corpo">
          {questoes.length === 0 ? (
            <p className="empty">Nenhuma nota salva — só texto.</p>
          ) : (
            <table className="aval-tabela">
              <thead>
                <tr>
                  <th>Questão</th>
                  <th className="aval-col-nota">Nota</th>
                </tr>
              </thead>
              <tbody>
                {questoes.map((q) => (
                  <tr key={q.chave}>
                    <td>
                      <strong>{q.titulo}</strong>
                      {q.descricao && <small>{q.descricao}</small>}
                    </td>
                    <td className="aval-col-nota">
                      <span className="aval-nota-mini">{respostas[q.chave]}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {(r.pontos_fortes || r.pontos_melhoria || r.comentario) && (
            <div className="aval-textos">
              {r.pontos_fortes && (
                <div>
                  <h5>Pontos fortes</h5>
                  <p>{r.pontos_fortes}</p>
                </div>
              )}
              {r.pontos_melhoria && (
                <div>
                  <h5>Pontos de melhoria</h5>
                  <p>{r.pontos_melhoria}</p>
                </div>
              )}
              {r.comentario && (
                <div>
                  <h5>Observações</h5>
                  <p>{r.comentario}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
